import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, TrendingDown, TrendingUp, Package, Clock, ArrowRight, Filter } from 'lucide-react'
import { supabase, isDemoMode } from '../../lib/supabase'
import { DemoNotice } from '../../lib/demo'

// Staff-only account-health dashboard. Surfaces churn signals across the
// whole book: sites with big YoY spend drops, clients that stopped ordering
// categories, products absent >90 days, and a headline run-rate vs. prior.
// Not visible to client users.

function fmtMoney(n) { return '£' + Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) }
function fmtDate(s) { return s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—' }
function daysSince(s) {
  if (!s) return null
  const ms = Date.now() - new Date(s).getTime()
  return Math.floor(ms / 86400000)
}

const YOY_DROP_THRESHOLD = 20        // % below prior period counts as churn signal
const STALE_DAYS_THRESHOLD = 90       // Days since a category / product last ordered

export default function AccountHealth() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all | drops | dormant

  useEffect(() => {
    if (isDemoMode) { setLoading(false); return }
    (async () => {
      // Pull every real order line with its site + group + product category over
      // the last 24 months. Small volumes — no need to paginate for now.
      const { data, error } = await supabase.rpc('account_health_snapshot')
      if (!error && data) setRows(data)
      setLoading(false)
    })()
  }, [])

  const bySite = useMemo(() => {
    // Group into { site_id, site_name, group_name, spend_last12, spend_prior12,
    //   yoy_pct, last_order_at, categories: [{name, spend_last12, spend_prior12, last_ordered}] }
    const map = new Map()
    const now = Date.now()
    const oneY = 365 * 86400000
    for (const r of rows) {
      const key = r.site_id
      if (!map.has(key)) map.set(key, {
        site_id: r.site_id, site_name: r.site_name, group_name: r.group_name,
        spend_last12: 0, spend_prior12: 0, last_order_at: null,
        categoryMap: new Map(),
      })
      const row = map.get(key)
      const placed = new Date(r.placed_at || r.created_at).getTime()
      const sub = Number(r.subtotal || 0)
      const age = now - placed
      if (age <= oneY) row.spend_last12 += sub
      else if (age <= 2 * oneY) row.spend_prior12 += sub
      if (!row.last_order_at || placed > row.last_order_at) row.last_order_at = placed

      const cat = r.category || 'Uncategorised'
      if (!row.categoryMap.has(cat)) row.categoryMap.set(cat, { name: cat, spend_last12: 0, spend_prior12: 0, last_ordered: null })
      const c = row.categoryMap.get(cat)
      if (age <= oneY) c.spend_last12 += sub
      else if (age <= 2 * oneY) c.spend_prior12 += sub
      if (!c.last_ordered || placed > c.last_ordered) c.last_ordered = placed
    }
    return [...map.values()].map(row => {
      const yoy_pct = row.spend_prior12 > 0 ? ((row.spend_last12 - row.spend_prior12) / row.spend_prior12) * 100 : null
      const days_since = row.last_order_at ? Math.floor((now - row.last_order_at) / 86400000) : null
      const categories = [...row.categoryMap.values()]
        .map(c => ({
          ...c,
          days_since: c.last_ordered ? Math.floor((now - c.last_ordered) / 86400000) : null,
          dropped: c.spend_prior12 > 0 && c.spend_last12 === 0,
        }))
        .sort((a, b) => b.spend_prior12 - a.spend_prior12)
      const droppedCategories = categories.filter(c => c.dropped)
      const staleCategories = categories.filter(c => c.days_since != null && c.days_since >= STALE_DAYS_THRESHOLD && c.spend_prior12 > 0 && c.spend_last12 > 0)
      const signals = []
      if (yoy_pct != null && yoy_pct <= -YOY_DROP_THRESHOLD) signals.push({ kind: 'yoy_drop', label: `Spend ${Math.abs(yoy_pct).toFixed(0)}% below last year` })
      if (droppedCategories.length) signals.push({ kind: 'category_dropped', label: `${droppedCategories.length} category${droppedCategories.length === 1 ? '' : 'ies'} stopped: ${droppedCategories.map(c => c.name).join(', ')}` })
      if (staleCategories.length) signals.push({ kind: 'category_stale', label: `${staleCategories.length} category${staleCategories.length === 1 ? '' : 'ies'} slowed >90 days` })
      if (days_since != null && days_since > 90 && (row.spend_prior12 > 0 || row.spend_last12 > 0)) signals.push({ kind: 'dormant', label: `${days_since} days since last order` })
      const missing_run_rate = yoy_pct != null && yoy_pct < 0 ? row.spend_prior12 - row.spend_last12 : 0
      return { ...row, categories, yoy_pct, days_since, signals, missing_run_rate, droppedCategories, staleCategories }
    })
    .filter(r => r.spend_last12 > 0 || r.spend_prior12 > 0)
    .sort((a, b) => (b.missing_run_rate || 0) - (a.missing_run_rate || 0))
  }, [rows])

  const totals = useMemo(() => {
    let last12 = 0, prior12 = 0, missing = 0
    let sitesDropped = 0, sitesDormant = 0, sitesStable = 0
    for (const s of bySite) {
      last12 += s.spend_last12
      prior12 += s.spend_prior12
      if (s.yoy_pct != null && s.yoy_pct <= -YOY_DROP_THRESHOLD) sitesDropped++
      if (s.days_since != null && s.days_since > 90) sitesDormant++
      if (s.signals.length === 0) sitesStable++
      missing += s.missing_run_rate
    }
    return { last12, prior12, missing, sitesDropped, sitesDormant, sitesStable, totalSites: bySite.length }
  }, [bySite])

  const filtered = useMemo(() => {
    if (filter === 'drops') return bySite.filter(s => s.yoy_pct != null && s.yoy_pct <= -YOY_DROP_THRESHOLD)
    if (filter === 'dormant') return bySite.filter(s => s.days_since != null && s.days_since > 90)
    return bySite.filter(s => s.signals.length > 0)
  }, [bySite, filter])

  return (
    <div className="space-y-4 max-w-6xl">
      <DemoNotice />

      <div className="card">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-5 h-5 text-brand" />
          <h2 className="text-lg font-semibold text-gray-900">Account health</h2>
        </div>
        <p className="text-sm text-gray-500">
          Churn signals across every client site: year-on-year spend drops, dormant sites, categories that stopped ordering,
          and the missing run-rate they represent. Rolling 12 months vs the equivalent 12 months prior.
        </p>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="stat-tile min-w-0">
          <div className="section-label flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Missing run-rate</div>
          <div className="text-2xl font-bold text-red-600 tabular-nums leading-tight break-words">{fmtMoney(totals.missing)}</div>
          <div className="text-xs text-gray-500 mt-1">£ below last-year equivalent</div>
        </div>
        <div className="stat-tile min-w-0">
          <div className="section-label flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Last 12 mo</div>
          <div className="text-2xl font-bold text-gray-900 tabular-nums leading-tight break-words">{fmtMoney(totals.last12)}</div>
          <div className="text-xs text-gray-500 mt-1">vs {fmtMoney(totals.prior12)} prior</div>
        </div>
        <div className="stat-tile min-w-0">
          <div className="section-label">Sites dropping ≥{YOY_DROP_THRESHOLD}%</div>
          <div className="text-2xl font-bold text-red-600 tabular-nums leading-tight">{totals.sitesDropped}</div>
        </div>
        <div className="stat-tile min-w-0">
          <div className="section-label">Dormant &gt;90 days</div>
          <div className="text-2xl font-bold text-amber-600 tabular-nums leading-tight">{totals.sitesDormant}</div>
        </div>
        <div className="stat-tile min-w-0">
          <div className="section-label">Stable</div>
          <div className="text-2xl font-bold text-emerald-600 tabular-nums leading-tight">{totals.sitesStable}</div>
          <div className="text-xs text-gray-500 mt-1">of {totals.totalSites}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="card !py-3">
        <div className="flex items-center gap-3 flex-wrap text-sm">
          <span className="text-gray-500 flex items-center gap-1"><Filter className="w-4 h-4" /> Show</span>
          {[
            { key: 'all',     label: `All with signals (${bySite.filter(s => s.signals.length > 0).length})` },
            { key: 'drops',   label: `YoY drops (${totals.sitesDropped})` },
            { key: 'dormant', label: `Dormant (${totals.sitesDormant})` },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1 rounded-full border ${filter === f.key ? 'bg-brand text-white border-brand' : 'bg-white text-gray-700 border-gray-200 hover:border-brand'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No sites in this filter. Everyone's ordering as expected.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-gray-500 border-b border-gray-100">
                  <th className="text-left py-2">Site</th>
                  <th className="text-left py-2 hidden md:table-cell">Group</th>
                  <th className="text-right py-2 w-28">Last 12mo</th>
                  <th className="text-right py-2 w-28">Prior 12mo</th>
                  <th className="text-right py-2 w-20">YoY</th>
                  <th className="text-right py-2 w-24 hidden sm:table-cell">Missing £</th>
                  <th className="text-right py-2 w-24 hidden sm:table-cell">Last order</th>
                  <th className="text-left py-2 hidden lg:table-cell">Signals</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => (
                  <tr key={s.site_id} className="border-b border-gray-50 align-top">
                    <td className="py-2 font-medium text-gray-800">
                      <Link to={`/companies/${s.site_id}`} className="hover:text-brand-dark">{s.site_name}</Link>
                    </td>
                    <td className="py-2 text-gray-600 hidden md:table-cell">{s.group_name || '—'}</td>
                    <td className="py-2 text-right tabular-nums">{fmtMoney(s.spend_last12)}</td>
                    <td className="py-2 text-right tabular-nums text-gray-500">{fmtMoney(s.spend_prior12)}</td>
                    <td className={`py-2 text-right tabular-nums font-medium ${s.yoy_pct == null ? 'text-gray-400' : s.yoy_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {s.yoy_pct == null ? '—' : (s.yoy_pct >= 0 ? '+' : '') + s.yoy_pct.toFixed(0) + '%'}
                    </td>
                    <td className={`py-2 text-right tabular-nums hidden sm:table-cell ${s.missing_run_rate > 0 ? 'text-red-700 font-medium' : 'text-gray-400'}`}>
                      {s.missing_run_rate > 0 ? fmtMoney(s.missing_run_rate) : '—'}
                    </td>
                    <td className="py-2 text-right hidden sm:table-cell">
                      <span className={s.days_since != null && s.days_since > 90 ? 'text-amber-600 font-medium' : 'text-gray-600'}>
                        {s.days_since == null ? '—' : `${s.days_since}d`}
                      </span>
                    </td>
                    <td className="py-2 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {s.signals.map((sig, i) => (
                          <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                            sig.kind === 'yoy_drop'         ? 'bg-red-50 text-red-700' :
                            sig.kind === 'category_dropped' ? 'bg-amber-50 text-amber-700' :
                            sig.kind === 'category_stale'   ? 'bg-yellow-50 text-yellow-700' :
                            sig.kind === 'dormant'          ? 'bg-orange-50 text-orange-700' :
                            'bg-gray-100 text-gray-700'
                          }`} title={sig.label}>{sig.label}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
